import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Users, Calendar, Mail, Shield, Clock, MousePointer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import favicon from '/public/favicon.svg';
import demoThumbnail from '/public/lovable-uploads/munger-demo-thumbnail.jpg';

interface ClickerHeroSectionProps {
  onStartClick?: () => void;
}

const ClickerHeroSection: React.FC<ClickerHeroSectionProps> = ({ onStartClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [hoursWasted, setHoursWasted] = useState(847);
  const navigate = useNavigate();

  // Increment click counter
  useEffect(() => {
    const interval = setInterval(() => {
      setClickCount(prev => prev + 1);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  // Handler for sign in
  const handleSignIn = () => {
    navigate('/login');
  };

  // Sticky navigation with dual CTAs
  const StickyNav = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src={favicon} 
            alt="Munger.ai" 
            className="h-8 w-8 mr-2"
          />
          <span className="font-bold text-lg sm:text-xl text-gray-900">Munger.ai</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleSignIn}
            className="text-gray-600 hover:text-gray-900 text-xs sm:text-sm px-3 sm:px-4"
          >
            Sign In
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/book-demo')}
            className="border-gray-300 hover:border-gray-400 text-xs sm:text-sm px-3 sm:px-4"
          >
            <span className="hidden sm:inline">Book </span>Demo
          </Button>
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4" 
            onClick={onStartClick}
          >
            Start Free<span className="hidden sm:inline"> →</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Sticky Navigation */}
      <StickyNav />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="text-center">
          {/* Click counter */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8"
          >
            <Badge className="bg-red-900/30 text-red-300 border-red-800 text-xs sm:text-sm">
              <MousePointer className="w-3 h-3 mr-1" />
              Click count: {clickCount}
            </Badge>
            <Badge className="bg-orange-900/30 text-orange-300 border-orange-800 text-xs sm:text-sm">
              <Clock className="w-3 h-3 mr-1" />
              Hours wasted: {hoursWasted}
            </Badge>
          </motion.div>

          {/* Main content wrapper */}
          <div className="relative">
            {/* Clear value proposition */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight px-4"
            >
              Click. Click. Click. That's All You've Become.
            </motion.h1>

            {/* Supporting copy - concrete benefits */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto px-4"
            >
              You weren't born to manage Apollo.io. You were born to BUILD.
            </motion.p>

            {/* Click animation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="max-w-3xl mx-auto mb-12 relative h-32"
            >
              {/* Animated mouse cursor and clicks */}
              <div className="absolute inset-0 overflow-hidden">
                {Array.from({ length: 15 }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0, x: Math.random() * 100 - 50 + '%', y: Math.random() * 100 - 50 + '%' }}
                    animate={{ 
                      opacity: [0, 1, 0], 
                      scale: [0, 1, 0.8],
                      x: Math.random() * 100 - 50 + '%', 
                      y: Math.random() * 100 - 50 + '%'
                    }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity, 
                      repeatType: 'loop',
                      delay: i * 0.2,
                      ease: "easeInOut"
                    }}
                    className="absolute w-8 h-8 flex items-center justify-center"
                  >
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <MousePointer className="w-4 h-4 text-red-400" />
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Breaking chains animation on hover */}
              <motion.div 
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                whileHover={{ scale: 1.2 }}
              >
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-lg">AI</span>
                  </div>
                  <motion.div 
                    className="absolute -top-4 -left-4 w-24 h-24 border-4 border-dashed border-gray-600 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.div 
                    className="absolute -bottom-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center"
                    whileHover={{ scale: 1.5, backgroundColor: "#22c55e" }}
                  >
                    <span className="text-white font-bold">✓</span>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero video with play button overlay */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="relative max-w-4xl mx-auto mb-12"
            >
              {!showVideo ? (
                <div 
                  className="relative cursor-pointer group"
                  onClick={() => setShowVideo(true)}
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Video thumbnail */}
                    <img 
                      src={demoThumbnail} 
                      alt="Munger AI Demo - Automated Pipeline"
                      className="w-full h-full object-cover opacity-70"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                        <Play className="w-12 h-12 text-blue-600 ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                      See how AI replaces 847+ hours of clicking
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video rounded-2xl shadow-2xl overflow-hidden">
                  <iframe 
                    src="https://www.loom.com/embed/97abf599533e4e81bf82399f0b5b6de3?sid=dc70419a-250e-4407-8357-ffe11dcb59b0&autoplay=1"
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
              )}
            </motion.div>

            {/* Dual CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={onStartClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Break Free Today →
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/book-demo')}
                  className="border-2 border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white px-8 py-6 text-lg rounded-lg"
                >
                  Book Demo
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-xs sm:text-sm text-gray-400 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4"
            >
              <span>✅ 30-day money-back guarantee</span>
              <span className="hidden sm:inline">✅ Setup in under 10 minutes</span>
              <span>✅ Cancel anytime</span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClickerHeroSection; 